import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { v4 as uuidv4 } from 'uuid';

import { getPaginatedDealers, setPaginatedDealersFilters } from 'modules/dealers/actions';
import {
  getIds,
  getPage,
  getParams,
  hasNextPage,
  isLoading as isLoadingSelector
} from 'modules/dealers/selectors/select';
import { getSelectedIntervention } from 'modules/interventions/selectors';
import { getCurrentUserDealerIds } from 'modules/users/selectors/currentUser';
import { hasAccessToDealer } from 'selectors/dealerSelectors';
import { Select } from 'ui/base';
import { uniq } from 'utils/ObjectUtils';

import OptionContent from './component/Option';

/**
 * @typedef {{
 *    autoSelect?: boolean,
 *    allowDefaultValue?:boolean,
 *    params?: import('modules/dealers/types').InitializeDealerSelectorParamsPayload
 * } & import('antd/lib/select').SelectProps<string | Array<string>>} PaginatedDealerSelectProps
 */

/** @type {React.FC<PaginatedDealerSelectProps>} */
const PaginatedDealerSelect = ({
  value,
  autoSelect = false,
  allowClear = false,
  allowDefaultValue = false,
  params: baseParameters = {},
  ...inputProps
}) => {
  const dispatch = useDispatch();
  const { current: token } = React.useRef(uuidv4());

  const params = useSelector(state => getParams(state, token));
  const loadedDealer = useSelector(state => getIds(state, token));
  const canLoadMore = useSelector(state => hasNextPage(state, token));
  const { number: page } = useSelector(state => getPage(state, token));
  const isLoading = useSelector(state => isLoadingSelector(state, token));
  const userDealerIds = useSelector(getCurrentUserDealerIds);
  const intervention = useSelector(getSelectedIntervention);
  const canAccessToDealer = useSelector(state => hasAccessToDealer(state, intervention?.dealer?.id));

  // Specific to the given value
  const dealerIds = Array.isArray(value) ? value : [value];
  // selected dealer not present in the dealerCollection
  const dealersIdToAppend = dealerIds.filter(dealerId => !!dealerId && !loadedDealer.includes(dealerId));
  let allIds = uniq([...loadedDealer, ...dealersIdToAppend]);

  if (!canAccessToDealer) {
    allIds = userDealerIds ? allIds.filter(dealerId => userDealerIds.includes(dealerId)) : allIds;
  }

  // If the component configuration change
  useDeepCompareEffect(() => {
    dispatch(setPaginatedDealersFilters(token, baseParameters));
  }, [baseParameters]);

  // When autoSelect is enabled
  React.useEffect(() => {
    const hasData = !isLoading && loadedDealer.length > 0;
    const mustChange = !value && autoSelect && hasData;
    if (mustChange) {
      inputProps?.onChange(loadedDealer[0]);
    }
  }, [autoSelect, isLoading]);

  /**
     * @param {string} search
     */
  const handleOnSearch = search => {
    dispatch(setPaginatedDealersFilters(token, { text: search }));
  };

  const handleLoadMore = () => {
    if (canLoadMore) {
      dispatch(getPaginatedDealers(token, { ...params, page: page + 1 }));
    }
  };

  /**
     * @param {boolean} open
     */
  const handleOnOpenChange = open => {
    if (!open && params.text?.length > 0) {
      dispatch(setPaginatedDealersFilters(token, { text: null }));
    }

    if (typeof inputProps.onOpenChange === 'function') {
      inputProps.onOpenChange(open);
    }
  };

  const labelRender = React.useCallback(
    ({ value: dealerId }) =>
      (dealerId ? (
        <OptionContent dealerId={dealerId} tooltip={false} />
      ) : (
        <FormattedMessage id="DealerSelect.default" defaultMessage="All dealers" />
      )),
    []
  );

  const options = React.useMemo(
    () => [
      ...(allowDefaultValue
        ? [{ value: '', label: <FormattedMessage id="DealerSelect.default" defaultMessage="All dealers" /> }]
        : []),
      ...allIds.map(dealerId => ({
        value: dealerId,
        label: <OptionContent dealerId={dealerId} tooltip />,
        'data-cy': `dealer-select-${dealerId}`
      }))
    ],
    [allowDefaultValue, allIds]
  );

  return (
    <Select
      showSearch
      value={value}
      options={options}
      loading={isLoading}
      filterOption={false}
      allowClear={allowClear}
      labelRender={labelRender}
      onSearch={handleOnSearch}
      loadMore={handleLoadMore}
      data-cy="dealers_dropdown"
      onOpenChange={handleOnOpenChange}
      {...inputProps}
    />
  );
};

export default PaginatedDealerSelect;
